
import {
    CONFERENCE_JOINED,
    CONFERENCE_LEFT,
    ENDPOINT_MESSAGE_RECEIVED,
    getCurrentConference
} from '../base/conference';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import {
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PARTICIPANT_ROLE_CHANGED
} from '../base/participants/actionTypes';
import { getParticipantById, isParticipantModerator } from '../base/participants/functions';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { TRACK_ADDED, TRACK_REMOVED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { selectParticipantInLargeVideo } from '../large-video/actions.any';
import { SET_PIP_ACTIVE } from '../mobile/picture-in-picture/actionTypes';

import { SET_FOCUS_MODE_ENABLED } from './actionTypes';
import {
    applyRemoteFocusMode,
    autoEnableFocusMode,
    reconcileFocusModeFromMetadata,
    setFocusModeEnabled
} from './actions.any';
import { SET_FOCUS_MODE_MESSAGE_TYPE } from './constants';
import {
    getFocusModeAllowedSourceNames,
    isFocusModeEnabled,
    shouldRestrictFocusModeView
} from './functions';
import logger from './logger';

/**
 * Participants and tracks arrive in bursts (a page of people joining, a
 * screenshare replacing a camera), and each one would otherwise rewrite the
 * bridge constraints. Coalesce them into one write.
 */
const CONSTRAINTS_COALESCE_MS = 150;

/**
 * Receive height used when the state carries no configured video quality.
 */
const DEFAULT_RECEIVER_MAX_HEIGHT = 1080;

/**
 * Receive height the bridge is handed back when Focus Mode stops restricting
 * this client. Matches the value the picture-in-picture middleware restores.
 */
const UNRESTRICTED_RECEIVER_MAX_HEIGHT = 2160;

let _constraintsTimer;
let _metadataUpdatedHandler;

/**
 * Whether this client has narrowed its bridge constraints at least once this
 * conference. Without it, every unrelated track or participant action in an
 * ordinary meeting would push a needless constraints reset.
 *
 * @type {boolean}
 */
let _hasRestricted = false;

/**
 * The receive-quality ceiling currently in force, so that Focus Mode narrows
 * *which* sources arrive without also overriding the quality the user chose.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {number}
 */
function _getReceiverMaxHeight(state) {
    const {
        maxReceiverVideoQuality,
        preferredVideoQuality
    } = state['features/video-quality'] || {};
    const configured = [ preferredVideoQuality, maxReceiverVideoQuality ]
        .filter(height => typeof height === 'number' && height > 0);

    return configured.length ? Math.min(...configured) : DEFAULT_RECEIVER_MAX_HEIGHT;
}

/**
 * Tells the bridge to send this client only the host video it is allowed to
 * see. This is what makes the restriction real rather than cosmetic: a peer's
 * camera or screenshare is never delivered here, so it cannot be recovered from
 * the DOM or the debug console either.
 *
 * @param {Object} store - The redux store.
 * @private
 * @returns {void}
 */
function _applyReceiverConstraints({ getState }) {
    const state = getState();
    const conference = getCurrentConference(state);

    if (typeof conference?.setReceiverConstraints !== 'function') {
        return;
    }

    // Picture-in-picture pins a single source of its own; leave it alone.
    if (state['features/mobile/picture-in-picture']?.isPictureInPictureActive) {
        return;
    }

    try {
        if (!shouldRestrictFocusModeView(state)) {
            if (_hasRestricted) {
                conference.setReceiverConstraints({
                    lastN: -1,
                    constraints: {},
                    selectedSources: [],
                    onStageSources: [],
                    defaultConstraints: { maxHeight: UNRESTRICTED_RECEIVER_MAX_HEIGHT }
                });
                _hasRestricted = false;
            }

            return;
        }

        const sourceNames = getFocusModeAllowedSourceNames(state);
        const maxHeight = _getReceiverMaxHeight(state);
        const constraints = {};

        sourceNames.forEach(sourceName => {
            constraints[sourceName] = { maxHeight };
        });

        conference.setReceiverConstraints({
            lastN: sourceNames.length,
            selectedSources: sourceNames,
            onStageSources: sourceNames,

            // Anything not named above is a non-host source and must not arrive.
            defaultConstraints: { maxHeight: 0 },
            constraints
        });
    } catch (e) {
        logger.warn('Failed to apply the Focus Mode receiver constraints', e);
    }
}

/**
 * Brings the local media and layout in line with the current Focus Mode state:
 * re-picks the large video if it is now showing somebody this client may not
 * see, and re-negotiates what the bridge sends.
 *
 * @param {Object} store - The redux store.
 * @private
 * @returns {void}
 */
function _applyFocusModePolicy(store) {
    const { dispatch, getState } = store;
    const restrict = shouldRestrictFocusModeView(getState());

    // This runs off global participant/track actions, so bail out early in the
    // overwhelmingly common case: an ordinary meeting with Focus Mode off.
    if (!restrict && !_hasRestricted) {
        return;
    }

    if (_constraintsTimer) {
        clearTimeout(_constraintsTimer);
        _constraintsTimer = undefined;
    }

    if (restrict) {
        _hasRestricted = true;
        _constraintsTimer = setTimeout(() => {
            _constraintsTimer = undefined;
            _applyReceiverConstraints(store);
        }, CONSTRAINTS_COALESCE_MS);
    } else {
        // Lifting the restriction should not wait.
        _applyReceiverConstraints(store);
    }

    dispatch(selectParticipantInLargeVideo(undefined, {
        selectionSource: 'focus-mode',
        forceSelection: true
    }));

    if (typeof APP !== 'undefined' && typeof APP.UI?.onParticipantsOrViewStateChange === 'function') {
        APP.UI.onParticipantsOrViewStateChange();
    }
}

/**
 * Whether the conference that is going away is being left only to join another
 * room of the same meeting.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {boolean}
 */
function _isSwitchingRooms(state) {
    const { isSwitchingBreakOutRoom, isSwitchingToMainRoom } = state['features/base/mtApi'] || {};

    return Boolean(isSwitchingBreakOutRoom || isSwitchingToMainRoom);
}

/**
 * Applies a Focus Mode command that arrived over the data channel, but only
 * when it genuinely came from a remote host.
 *
 * @param {Object} origin - The sender, as reported by lib-meet-hour.
 * @param {Object} message - The message payload.
 * @param {Object} store - The redux store.
 * @private
 * @returns {void}
 */
function _handleFocusModeMessage(origin, message, { dispatch, getState }) {
    if (message?.type !== SET_FOCUS_MODE_MESSAGE_TYPE || typeof message.enabled !== 'boolean') {
        return;
    }

    const senderId = origin?._id || origin?.getId?.() || message.senderId;
    const sender = senderId ? getParticipantById(getState(), senderId) : undefined;

    if (!sender || sender.local || !isParticipantModerator(sender)) {
        logger.warn('Ignoring a Focus Mode command that did not come from a remote host');

        return;
    }

    dispatch(applyRemoteFocusMode(message.enabled));
}

MiddlewareRegistry.register(store => next => action => {
    let restoreFocusModeAfterRoomSwitch = false;

    if (action.type === CONFERENCE_LEFT) {
        if (_constraintsTimer) {
            clearTimeout(_constraintsTimer);
            _constraintsTimer = undefined;
        }
        _hasRestricted = false;

        // Moving between the main room and a breakout room leaves one
        // conference and joins another, which would otherwise drop Focus Mode
        // on the floor: the breakout MUC carries none of the main room's
        // metadata, so nothing would turn it back on. Focus Mode is a setting
        // for the meeting, not for one room, so carry it across the switch.
        restoreFocusModeAfterRoomSwitch = _isSwitchingRooms(store.getState())
            && isFocusModeEnabled(store.getState());
    }

    const result = next(action);

    if (restoreFocusModeAfterRoomSwitch) {
        // Deliberately the plain setter rather than applyRemoteFocusMode: this
        // is the same decision carried forward, not a new one, so it must not
        // re-notify or re-open the explainer.
        store.dispatch(setFocusModeEnabled(true));
    }

    switch (action.type) {
    case SET_FOCUS_MODE_ENABLED:
    case PARTICIPANT_JOINED:
    case PARTICIPANT_LEFT:
    case TRACK_ADDED:
    case TRACK_REMOVED:
    case TRACK_UPDATED:
    case SET_PIP_ACTIVE:
        _applyFocusModePolicy(store);
        break;

    case PARTICIPANT_ROLE_CHANGED:
        if (action.participant?.local) {
            store.dispatch(autoEnableFocusMode());
        }
        _applyFocusModePolicy(store);
        break;

    case CONFERENCE_JOINED:
        store.dispatch(reconcileFocusModeFromMetadata());
        store.dispatch(autoEnableFocusMode());
        _applyFocusModePolicy(store);
        break;

    case ENDPOINT_MESSAGE_RECEIVED:
        _handleFocusModeMessage(action.participant, action.data, store);
        break;
    }

    return result;
});

/**
 * Follows the room metadata so that a Focus Mode set before this client joined -
 * or by a host on another device - is picked up.
 */
StateListenerRegistry.register(
    state => state['features/base/conference'].conference,
    (conference, { dispatch }, previousConference) => {
        if (previousConference && _metadataUpdatedHandler) {
            previousConference.off(MHConferenceEvents.METADATA_UPDATED, _metadataUpdatedHandler);
            _metadataUpdatedHandler = undefined;
        }

        if (conference && conference !== previousConference) {
            _metadataUpdatedHandler = () => dispatch(reconcileFocusModeFromMetadata());
            conference.on(MHConferenceEvents.METADATA_UPDATED, _metadataUpdatedHandler);
        }
    });
